import { useState } from "react";
import { toast, Toaster } from "sonner";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Award, Loader2, Upload, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { SellerLicenseRoute } from "../components/SellerLicenseRoute";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

export default function SellerEliteApply() {
  return (
    <SellerLicenseRoute>
      <SellerEliteApplyInner/>
    </SellerLicenseRoute>
  );
}

function SellerEliteApplyInner() {
  const { api } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({
    audited_turnover_3y: "", audited_turnover_2y: "", audited_turnover_1y: "",
    gst_rating: "A+", credit_rating: "AA",
    fleet_count: "", fleet_capacity_tons: "",
    warehouse_count: "", warehouse_sqft: "",
    msme_cert_no: "", iso_cert_no: "",
    safety_audit_date: "", insurance_summary: "",
    key_customers: "",
    financial_statement_b64: "", financial_statement_name: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const onFile = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File exceeds 10 MB"); return; }
    const b64 = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res((r.result || "").toString().split(",")[1] || "");
      r.onerror = rej; r.readAsDataURL(file);
    });
    setF(p => ({ ...p, financial_statement_b64: b64, financial_statement_name: file.name }));
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/seller/elite/apply", {
        audited_turnover_3y: Number(f.audited_turnover_3y),
        audited_turnover_2y: Number(f.audited_turnover_2y),
        audited_turnover_1y: Number(f.audited_turnover_1y),
        gst_rating: f.gst_rating, credit_rating: f.credit_rating,
        fleet_count: Number(f.fleet_count),
        fleet_capacity_tons: Number(f.fleet_capacity_tons),
        warehouse_count: Number(f.warehouse_count),
        warehouse_sqft: Number(f.warehouse_sqft),
        msme_cert_no: f.msme_cert_no, iso_cert_no: f.iso_cert_no,
        safety_audit_date: f.safety_audit_date,
        insurance_summary: f.insurance_summary,
        key_customers: (f.key_customers || "").split(",").map(s => s.trim()).filter(Boolean),
        financial_statement_b64: f.financial_statement_b64 || null,
        financial_statement_name: f.financial_statement_name || null,
      });
      toast.success("Elite application submitted · awaiting scrutiny");
      nav("/supplier/dashboard");
    } catch (e) { toast.error(e.response?.data?.detail || "Submission failed"); }
    finally { setBusy(false); }
  };

  const ready = f.audited_turnover_3y && f.audited_turnover_2y && f.audited_turnover_1y
                && f.gst_rating && f.credit_rating && f.fleet_count && f.fleet_capacity_tons
                && f.warehouse_count && f.warehouse_sqft && f.insurance_summary;

  return (
    <div className="min-h-screen bg-[#050506] text-[#F5F5F0]">
      <Toaster theme="dark" richColors position="top-right" />
      <Header />
      <main className="max-w-4xl mx-auto px-6 md:px-12 pt-28 pb-24">
        <Link to="/supplier/dashboard" className="inline-flex items-center gap-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] hover:text-[#D8B23E] mb-6">
          <ArrowLeft size={12}/> Back to dashboard
        </Link>

        <div className="flex items-start gap-4 border-b border-white/10 pb-6 mb-8">
          <div className="h-12 w-12 rounded-sm bg-[#D8B23E]/15 text-[#D8B23E] flex items-center justify-center shrink-0">
            <Award size={20}/>
          </div>
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Elite Scrutiny · Grand Slam</div>
            <h1 className="font-serif text-3xl">The strict elite application.</h1>
            <p className="font-body text-sm text-[#8A8A82] mt-2 max-w-2xl">
              Every field below is verified by Vinci Ops. Elite status carries priority in RFQ distribution and access to
              Vinci's largest deals. Decision within 5 business days.
            </p>
          </div>
        </div>

        <div className="space-y-6" data-testid="elite-application-form">
          <Section title="Financial history">
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Audited turnover · year -3 (INR)" v={f.audited_turnover_3y} onChange={v => set("audited_turnover_3y", v)} type="number" testid="e-t3"/>
              <Field label="Audited turnover · year -2 (INR)" v={f.audited_turnover_2y} onChange={v => set("audited_turnover_2y", v)} type="number" testid="e-t2"/>
              <Field label="Audited turnover · year -1 (INR)" v={f.audited_turnover_1y} onChange={v => set("audited_turnover_1y", v)} type="number" testid="e-t1"/>
              <Field label="GST rating (A+ / A / B / C)" v={f.gst_rating} onChange={v => set("gst_rating", v)} testid="e-gst"/>
              <Field label="External credit rating" v={f.credit_rating} onChange={v => set("credit_rating", v)} testid="e-credit"/>
              <div>
                <div className="font-body text-[11px] text-[#8A8A82] mb-1">Financial statement PDF (≤10 MB)</div>
                <label className="cursor-pointer inline-flex items-center gap-2 rounded-sm border border-white/15 hover:border-[#D8B23E] px-3 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] w-full">
                  <Upload size={11}/> {f.financial_statement_name ? f.financial_statement_name.slice(0, 30) : "Choose file"}
                  <input data-testid="e-financial-file" type="file" accept="application/pdf"
                         onChange={e => onFile(e.target.files?.[0])} className="hidden"/>
                </label>
              </div>
            </div>
          </Section>

          <Section title="Fleet & warehouse capacity">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Fleet count (owned trucks)" v={f.fleet_count} onChange={v => set("fleet_count", v)} type="number" testid="e-fleet-count"/>
              <Field label="Total fleet capacity (tons)" v={f.fleet_capacity_tons} onChange={v => set("fleet_capacity_tons", v)} type="number" testid="e-fleet-cap"/>
              <Field label="Warehouse count" v={f.warehouse_count} onChange={v => set("warehouse_count", v)} type="number" testid="e-wh-count"/>
              <Field label="Total warehouse sqft" v={f.warehouse_sqft} onChange={v => set("warehouse_sqft", v)} type="number" testid="e-wh-sqft"/>
            </div>
          </Section>

          <Section title="Compliance & safety">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="MSME certificate #" v={f.msme_cert_no} onChange={v => set("msme_cert_no", v)} testid="e-msme"/>
              <Field label="ISO certification #" v={f.iso_cert_no} onChange={v => set("iso_cert_no", v)} testid="e-iso"/>
              <Field label="Last safety audit date" v={f.safety_audit_date} onChange={v => set("safety_audit_date", v)} type="date" testid="e-safety"/>
              <Field label="Insurance summary (cover value + provider)" v={f.insurance_summary} onChange={v => set("insurance_summary", v)} testid="e-insurance"/>
            </div>
          </Section>

          <Section title="Key customers">
            <Field label="Comma-separated list of anchor customers (min 3)" v={f.key_customers}
                   onChange={v => set("key_customers", v)} testid="e-customers"/>
          </Section>

          <div className="flex items-center justify-between border-t border-white/10 pt-6">
            <div className="font-body text-[11px] text-[#8A8A82]">All fields verified · false statements void Elite membership.</div>
            <button onClick={submit} disabled={!ready || busy} data-testid="elite-submit"
                    className={`font-grotesk text-[10px] uppercase tracking-[0.24em] px-5 py-3 rounded-sm inline-flex items-center gap-2 ${
                      ready && !busy ? "bg-[#D8B23E] hover:bg-[#c5a233] text-black" : "bg-white/5 text-[#8A8A82] cursor-not-allowed"
                    }`}>
              {busy ? <Loader2 size={12} className="animate-spin"/> : <Award size={12}/>}
              Submit for scrutiny
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-5">
      <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E] mb-3">{title}</div>
      {children}
    </div>
  );
}
function Field({ label, v, onChange, type = "text", testid }) {
  return (
    <div>
      <div className="font-body text-[11px] text-[#8A8A82] mb-1">{label}</div>
      <input data-testid={testid} type={type} value={v} onChange={e => onChange(e.target.value)}
             className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#D8B23E]"/>
    </div>
  );
}
